import { useState, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../stores';
import { VideoDisplayState, VideoDisplayActions, DisplayMode, MaximizedWhiteboardState } from '../types/LiveKitVideoDisplay.types';
import { 
  LIVEKIT_TOGGLE_AUDIO_EVENT, 
  LIVEKIT_TOGGLE_VIDEO_EVENT, 
  LIVEKIT_TOGGLE_SCREEN_SHARE_EVENT,
  LIVEKIT_AUDIO_STATE_CHANGED,
  LIVEKIT_VIDEO_STATE_CHANGED,
  LIVEKIT_SCREEN_SHARE_STATE_CHANGED
} from '../styles/LiveKitVideoDisplay.styles';
// A user's toggle is a choice: writing through here drops any "forced off"
// marker the Join screen left, so a later deliberate off sticks.
import { writeMediaPreference } from '../../../utils/mediaPreferences';
import { readVideoToggleBusy } from '../../../services/liveKitVideoBackground';

export const displayModeCycle: DisplayMode[] = ['overlay', 'sidebar-right', 'top', 'sidebar-left', 'gallery'];

export const DISPLAY_MODE_STORAGE_KEY = 'preferDisplayMode';
const DEFAULT_DISPLAY_MODE: DisplayMode = 'overlay';

/**
 * SC-375: the map layouts are remembered per browser. The gallery never is: arriving in a room with the
 * map hidden and movement locked is disorienting, so a visit always starts on the last map layout.
 */
const isRememberedDisplayMode = (value: unknown): value is DisplayMode =>
  typeof value === 'string' && value !== 'gallery' && (displayModeCycle as string[]).includes(value);

/** Storage can be missing or blocked, so fall back quietly. */
export function readStoredDisplayMode(): DisplayMode {
  try {
    const stored = localStorage.getItem(DISPLAY_MODE_STORAGE_KEY);
    return isRememberedDisplayMode(stored) ? stored : DEFAULT_DISPLAY_MODE;
  } catch {
    return DEFAULT_DISPLAY_MODE;
  }
}

function storeDisplayMode(mode: DisplayMode): void {
  if (!isRememberedDisplayMode(mode)) return;
  try {
    localStorage.setItem(DISPLAY_MODE_STORAGE_KEY, mode);
  } catch {
    // Not remembering the layout is harmless.
  }
}

export const useVideoDisplayState = (): [VideoDisplayState, VideoDisplayActions] => {
  // Initialize state from localStorage preferences (missing = enabled, same as PermissionsDialog)
  const [audioEnabled, setAudioEnabled] = useState(() => {
    return localStorage.getItem('preferAudioEnabled') !== 'false';
  });
  
  const [videoEnabled, setVideoEnabled] = useState(() => {
    return localStorage.getItem('preferVideoEnabled') !== 'false';
  });
  
  const [screenShareEnabled, setScreenShareEnabled] = useState(false);
  const [displayMode, setDisplayModeState] = useState<DisplayMode>(readStoredDisplayMode);
  // The last non-gallery layout, for "Back to map".
  const lastMapDisplayModeRef = useRef<DisplayMode>(DEFAULT_DISPLAY_MODE);
  const [isAutoMuted, setIsAutoMuted] = useState(false);
  const [autoMuteReason, setAutoMuteReason] = useState<string>('');
  const [maximizedScreenShare, setMaximizedScreenShare] = useState<{
    participantId: string;
    trackSid: string;
    screenShareSessionId: string;
  } | null>(null);
  const [maximizedWhiteboard, setMaximizedWhiteboard] = useState<MaximizedWhiteboardState | null>(null);
  const [showErrorBanner, setShowErrorBanner] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);

  // Add loading state check to prevent rendering during loading
  const isLoading = useSelector((state: RootState) => state.game.loadingGame);

  // Listen for audio/video toggle events from LiveKitDebugDisplay and sync from LiveKit state
  useEffect(() => {
    // Handle audio toggle requests from LiveKitDebugDisplay
    const handleAudioToggle = (event: CustomEvent<{ enabled: boolean }>) => {
      setAudioEnabled(event.detail.enabled);
      writeMediaPreference('audio', event.detail.enabled);
    };

    // Handle video toggle requests from LiveKitDebugDisplay
    const handleVideoToggle = (event: CustomEvent<{ enabled: boolean }>) => {
      setVideoEnabled(event.detail.enabled);
      writeMediaPreference('video', event.detail.enabled);
    };

    // Handle screen share toggle requests
    const handleScreenShareToggle = (event: CustomEvent<{ enabled: boolean }>) => {
      setScreenShareEnabled(event.detail.enabled);
    };

    // Sync overlay from actual LiveKit state (fix false muted/disabled)
    const handleAudioStateChanged = (event: CustomEvent<{ enabled: boolean }>) => {
      setAudioEnabled(event.detail.enabled);
    };
    const handleVideoStateChanged = (event: CustomEvent<{ enabled: boolean }>) => {
      setVideoEnabled(event.detail.enabled);
    };
    const handleScreenShareStateChanged = (event: CustomEvent<{ enabled: boolean }>) => {
      setScreenShareEnabled(event.detail.enabled);
    };

    window.addEventListener(LIVEKIT_TOGGLE_AUDIO_EVENT, handleAudioToggle as EventListener);
    window.addEventListener(LIVEKIT_TOGGLE_VIDEO_EVENT, handleVideoToggle as EventListener);
    window.addEventListener(LIVEKIT_TOGGLE_SCREEN_SHARE_EVENT, handleScreenShareToggle as EventListener);
    window.addEventListener(LIVEKIT_AUDIO_STATE_CHANGED, handleAudioStateChanged as EventListener);
    window.addEventListener(LIVEKIT_VIDEO_STATE_CHANGED, handleVideoStateChanged as EventListener);
    window.addEventListener(LIVEKIT_SCREEN_SHARE_STATE_CHANGED, handleScreenShareStateChanged as EventListener);

    return () => {
      window.removeEventListener(LIVEKIT_TOGGLE_AUDIO_EVENT, handleAudioToggle as EventListener);
      window.removeEventListener(LIVEKIT_TOGGLE_VIDEO_EVENT, handleVideoToggle as EventListener);
      window.removeEventListener(LIVEKIT_TOGGLE_SCREEN_SHARE_EVENT, handleScreenShareToggle as EventListener);
      window.removeEventListener(LIVEKIT_AUDIO_STATE_CHANGED, handleAudioStateChanged as EventListener);
      window.removeEventListener(LIVEKIT_VIDEO_STATE_CHANGED, handleVideoStateChanged as EventListener);
      window.removeEventListener(LIVEKIT_SCREEN_SHARE_STATE_CHANGED, handleScreenShareStateChanged as EventListener);
    };
  }, []);

  // Actions
  const toggleAudio = useCallback(() => {
    const newState = !audioEnabled;
    setAudioEnabled(newState);

    // Save to localStorage for persistence
    writeMediaPreference('audio', newState);

    // Clear auto-mute state when user manually toggles audio
    if (isAutoMuted) {
      setIsAutoMuted(false);
      setAutoMuteReason('');
    }

    // Dispatch event to notify LiveKitRoomConnector
    window.dispatchEvent(
      new CustomEvent(LIVEKIT_TOGGLE_AUDIO_EVENT, {
        detail: { enabled: newState },
      })
    );
  }, [audioEnabled, isAutoMuted]);

  const toggleVideo = useCallback(() => {
    const newState = !videoEnabled;
    // The connector drops a camera-on while the background pipeline rebuilds. Refused here, before
    // anything is shown or saved: once the choice is stored and announced, every listener of the
    // toggle event (this hook's own included) would write it again, in an order no one controls,
    // and undo any correction made afterwards. "Off" is never refused.
    if (newState && readVideoToggleBusy()) return;
    setVideoEnabled(newState);
    
    // Save to localStorage for persistence
    writeMediaPreference('video', newState);
    
    // Dispatch event to notify LiveKitRoomConnector
    window.dispatchEvent(
      new CustomEvent(LIVEKIT_TOGGLE_VIDEO_EVENT, {
        detail: { enabled: newState },
      })
    );
  }, [videoEnabled]);

  const toggleScreenShare = useCallback(() => {
    const newState = !screenShareEnabled;
    setScreenShareEnabled(newState);
    
    // Dispatch event to notify LiveKitRoomConnector
    window.dispatchEvent(
      new CustomEvent(LIVEKIT_TOGGLE_SCREEN_SHARE_EVENT, {
        detail: { enabled: newState },
      })
    );

    // If disabling screen share, also clear any maximized view
    if (!newState) {
      setMaximizedScreenShare(null);
    }
  }, [screenShareEnabled]);

  /**
   * Change the screen share source: stops the current share (without closing
   * the modal) and immediately fires a new "start" event so the browser's
   * native source-picker dialog re-opens.
   */
  const changeScreenShareSource = useCallback(() => {
    // Stop the current share — do NOT clear maximizedScreenShare so modal stays open
    setScreenShareEnabled(false);
    window.dispatchEvent(
      new CustomEvent(LIVEKIT_TOGGLE_SCREEN_SHARE_EVENT, { detail: { enabled: false } })
    );

    // After LiveKit has time to unpublish the old track, immediately start a new share
    setTimeout(() => {
      setScreenShareEnabled(true);
      window.dispatchEvent(
        new CustomEvent(LIVEKIT_TOGGLE_SCREEN_SHARE_EVENT, { detail: { enabled: true } })
      );
    }, 600);
  }, []);

  const applyDisplayMode = useCallback((mode: DisplayMode) => {
    setDisplayModeState(mode);
    storeDisplayMode(mode);
  }, []);

  useEffect(() => {
    if (displayMode !== 'gallery') lastMapDisplayModeRef.current = displayMode;
  }, [displayMode]);

  const toggleDisplayMode = useCallback(() => {
    const idx = displayModeCycle.indexOf(displayMode);
    applyDisplayMode(displayModeCycle[(idx + 1) % displayModeCycle.length]);
  }, [displayMode, applyDisplayMode]);

  const handleSetDisplayMode = useCallback((mode: DisplayMode) => {
    applyDisplayMode(mode);
  }, [applyDisplayMode]);

  const leaveGallery = useCallback(() => {
    applyDisplayMode(lastMapDisplayModeRef.current);
  }, [applyDisplayMode]);

  const maximizeScreenShare = useCallback((participantId: string, trackSid: string, screenShareSessionId?: string) => {
    setMaximizedScreenShare({ participantId, trackSid, screenShareSessionId: screenShareSessionId || '' });
    setMaximizedWhiteboard(null); // Only one maximized at a time
  }, []);

  const minimizeScreenShare = useCallback(() => {
    setMaximizedScreenShare(null);
  }, []);

  const maximizeWhiteboardAction = useCallback((whiteboardId: string, participantId: string, whiteboardUrl: string) => {
    setMaximizedWhiteboard({ whiteboardId, participantId, whiteboardUrl });
    setMaximizedScreenShare(null); // Only one maximized at a time
  }, []);

  const minimizeWhiteboard = useCallback(() => {
    setMaximizedWhiteboard(null);
  }, []);

  const openPermissionsDialog = useCallback(() => {
    setShowPermissionsDialog(true);
    setShowErrorBanner(false);
  }, []);

  const hidePermissionsDialog = useCallback(() => {
    setShowPermissionsDialog(false);
  }, []);

  const handlePermissionsGranted = useCallback(() => {
    setShowPermissionsDialog(false);
    setShowErrorBanner(false);
    setErrorMessage(null);

    // Allow a moment for permissions to take effect
    setTimeout(() => {
      // Trigger a reconnect event
      window.dispatchEvent(new CustomEvent('livekit-manual-reconnect'));
      // Also trigger device reconnect
      window.dispatchEvent(new CustomEvent('livekit-manual-device-reconnect'));
    }, 500);
  }, []);

  // State object
  const state: VideoDisplayState = {
    audioEnabled,
    videoEnabled,
    screenShareEnabled,
    displayMode,
    isAutoMuted,
    autoMuteReason,
    maximizedScreenShare,
    maximizedWhiteboard,
    showErrorBanner,
    errorMessage,
    showPermissionsDialog,
  };

  // Actions object
  const actions: VideoDisplayActions = {
    toggleAudio,
    toggleVideo,
    toggleScreenShare,
    changeScreenShareSource,
    toggleDisplayMode,
    setDisplayMode: handleSetDisplayMode,
    leaveGallery,
    maximizeScreenShare,
    minimizeScreenShare,
    maximizeWhiteboard: maximizeWhiteboardAction,
    minimizeWhiteboard,
    openPermissionsDialog,
    hidePermissionsDialog,
    handlePermissionsGranted,
  };

  return [state, actions];
}; 
